import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useMemo } from "react";
import { RootState } from "../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import ChartCard, { ChartEmptyState } from "./ChartCard";
import ConcurrentLogoIcon from "../../../../../../../assets/svg/concurrent-logo-icon";
import PowerBiLogoPng from "../../../../../../../assets/images/power-bi-logo.png";
import HubspotLogoIcon from "../../../../../../../assets/svg/hubspot-logo-icon";
import FeatureRequestStarIcon from "../../../../../../../assets/svg/feature-request-star-icon";
import AddUserIcon from "../../../../../../../assets/svg/add-user-icon";
import PrismLogoPng from "../../../../../../../assets/images/prism-logo.png";
import AnalyticsIcon from "../../../../../../../assets/svg/analytics-icon";
import SettingsIcon from "../../../../../../../assets/svg/settings-icon";

export interface SystemChartDataItem {
  system: string;
  value: number;
  key: string;
}

interface TicketsBySystemChartProps {
  data: SystemChartDataItem[];
}

type TickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  yAxisWidth?: number;
};
type LabelProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number;
};

const ValueLabel = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  value,
}: LabelProps) => {
  if (!value) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      fill="#a3a3a3"
      fontSize={12}
      fontWeight={500}
      dominantBaseline="middle"
    >
      {value}
    </text>
  );
};

const ICON_WIDTH = 20;

const TicketsBySystemChart = ({ data }: TicketsBySystemChartProps) => {
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);
  const gridStroke = isDarkMode ? "#393f4a" : "#e9eaeb";
  const barFill = isDarkMode ? "#fbfbfb" : "#252b37";
  const labelColor = isDarkMode ? "#fbfbfb" : "#222222";

  const getIcon = (name: string) => {
    switch (name) {
      case "concurrent":
        return <ConcurrentLogoIcon className="w-4 h-3.5" />;
      case "power-bi":
        return (
          <img src={PowerBiLogoPng} alt="Power BI" className="w-4 h-3.5" />
        );
      case "hubspot":
        return <HubspotLogoIcon className="w-4 h-4" />;
      case "prism":
        return <img src={PrismLogoPng} alt="PRISM" className="w-4 h-3.5" />;
      case "feature-requests":
        return <FeatureRequestStarIcon className="w-4 h-3.5" />;
      case "new-user-access":
        return (
          <AddUserIcon className="w-4 h-3.5 text-verba-blue dark:text-verba-blue-dk" />
        );
      default:
        return (
          <SettingsIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
        );
    }
  };

  const maxValue = useMemo(() => {
    if (!data?.length) return 100;
    const max = Math.max(...data.map((s) => s.value));
    const step = max <= 10 ? 2 : max <= 50 ? 10 : max <= 200 ? 50 : 100;
    return Math.ceil(max / step) * step || step;
  }, [data]);

  const xTicks = useMemo(() => {
    const step = maxValue / 5;
    return Array.from({ length: 6 }, (_, i) => Math.round(i * step));
  }, [maxValue]);

  const yAxisWidth = useMemo(() => {
    if (!data?.length) return 80;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    let maxWidth = 0;
    for (const s of data) {
      const text = s.system ?? "";
      let textWidth: number;
      if (ctx) {
        ctx.font = "500 16px sans-serif";
        textWidth = ctx.measureText(text).width;
      } else {
        textWidth = text.length * 9;
      }
      maxWidth = Math.max(maxWidth, textWidth + ICON_WIDTH);
    }
    return Math.ceil(maxWidth) + 4;
  }, [data]);

  const SystemTick = ({
    x = 0,
    y = 0,
    payload,
    data: tickData,
  }: TickProps & { data: SystemChartDataItem[] }) => {
    const name = payload?.value ?? "";
    const key = tickData?.find((item) => item?.system === name)?.key ?? "";
    return (
      <g transform={`translate(${x},${y})`}>
        <foreignObject x={-yAxisWidth} y={-12} width={yAxisWidth} height={24}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "100%",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                color: labelColor,
                whiteSpace: "nowrap",
              }}
              className="flex items-center text-base gap-1"
            >
              {getIcon(key)}
              {name}
            </span>
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <ChartCard
      title={helpdeskLabels.ticketsBySystem}
      description="Distribution of issue types across all systems"
      headerIcon={
        <AnalyticsIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk" />
      }
      className="h-full"
    >
      {data?.length === 0 ? (
        <ChartEmptyState />
      ) : (
        <div
          className="mt-auto pb-4"
          style={{ height: data.length * 36 + 24 }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 30, bottom: 0, left: 0 }}
              barSize={8}
            >
              <CartesianGrid
                horizontal={false}
                vertical={false}
                stroke={gridStroke}
                strokeDasharray="3 3"
              />
              <XAxis
                type="number"
                domain={[0, maxValue]}
                ticks={xTicks}
                tickLine={false}
                axisLine={{ stroke: gridStroke }}
                tick={{
                  fill: isDarkMode ? "#fbfbfb" : "#252b37",
                  fontSize: 10,
                }}
              />
              <YAxis
                type="category"
                dataKey="system"
                width={yAxisWidth}
                tickLine={false}
                axisLine={false}
                tick={(props: TickProps) => (
                  <SystemTick {...props} data={data} />
                )}
                interval={0}
              />
              <Bar
                dataKey="value"
                fill={barFill}
                radius={[4, 4, 4, 4]}
                label={(props: LabelProps) => <ValueLabel {...props} />}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

export default TicketsBySystemChart;
